import React from 'react';
import './ConsoleAssistant.css';

type ChatMessage = { role: 'user' | 'assistant'; text: string; refused?: boolean };

export type ConsoleHelpSignal = {
  id: string;
  tipEn: string;
  tipEs: string;
  promptEn?: string;
  promptEs?: string;
  urgency?: 'soft' | 'strong';
};

type Props = {
  apiBase: string;
  accessToken: string;
  language: 'en' | 'es';
  activePage?: string;
  suggestedPrompts?: string[];
  /** Dashboard / formation cues that the operator likely needs help. */
  helpSignals?: ConsoleHelpSignal[];
};

const ASK_EVENT = 'nextcompli:console-assistant-ask';
const HELP_SIGNAL_EVENT = 'nextcompli:console-assistant-help';
const DISMISS_STORAGE_PREFIX = 'nextcompli.consoleAssistant.dismissed.';

export const askConsoleAssistant = (prompt: string) => {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new CustomEvent(ASK_EVENT, { detail: { prompt } }));
};

/** Make the Help launcher pulse + show a tip when something looks stuck. */
export const signalConsoleHelp = (signal: ConsoleHelpSignal) => {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new CustomEvent(HELP_SIGNAL_EVENT, { detail: signal }));
};

const readDismissed = (id: string): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.sessionStorage.getItem(`${DISMISS_STORAGE_PREFIX}${id}`) === '1';
  } catch {
    return false;
  }
};

const writeDismissed = (id: string) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.setItem(`${DISMISS_STORAGE_PREFIX}${id}`, '1');
  } catch {
    /* ignore quota */
  }
};

const ConsoleAssistant: React.FC<Props> = ({
  apiBase,
  accessToken,
  language,
  activePage = '',
  suggestedPrompts,
  helpSignals = [],
}) => {
  const es = language === 'es';
  const [open, setOpen] = React.useState(false);
  const [input, setInput] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [liveSignal, setLiveSignal] = React.useState<ConsoleHelpSignal | null>(null);
  const [dismissedIds, setDismissedIds] = React.useState<Set<string>>(() => new Set());
  const [messages, setMessages] = React.useState<ChatMessage[]>([{
    role: 'assistant',
    text: es
      ? 'Pregunte sobre los productos y pasos que esta cuenta ya puede ver. No inventaré acceso que no tenga.'
      : 'Ask about the products and steps this login can already see. I will not invent access you do not have.',
  }]);
  const sessionId = React.useRef(`console-${Math.random().toString(36).slice(2)}`);
  const bodyRef = React.useRef<HTMLDivElement | null>(null);
  const autoOpenedFor = React.useRef<string | null>(null);
  const defaults = suggestedPrompts && suggestedPrompts.length > 0
    ? suggestedPrompts
    : (es
      ? [
        '¿Cuál es el siguiente paso para mi empresa?',
        '¿Por qué no veo suscripciones?',
        '¿Qué hace Inicio en formación?',
      ]
      : [
        'What’s the next thing I need for my company?',
        'Why don’t I see any subscriptions?',
        'What does Formation Home do?',
      ]);

  const activeSignal = React.useMemo(() => {
    const candidates = [...helpSignals, ...(liveSignal ? [liveSignal] : [])];
    for (const signal of candidates) {
      if (!signal?.id) {
        continue;
      }
      if (dismissedIds.has(signal.id) || readDismissed(signal.id)) {
        continue;
      }
      return signal;
    }
    return null;
  }, [dismissedIds, helpSignals, liveSignal]);

  const needsHelp = Boolean(activeSignal) && !open;

  React.useEffect(() => {
    if (open && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, open, loading]);

  React.useEffect(() => {
    if (!activeSignal || open) {
      return;
    }
    if (activeSignal.urgency !== 'strong') {
      return;
    }
    if (autoOpenedFor.current === activeSignal.id) {
      return;
    }
    autoOpenedFor.current = activeSignal.id;
    const timer = window.setTimeout(() => {
      setOpen(true);
    }, 900);
    return () => window.clearTimeout(timer);
  }, [activeSignal, open]);

  const dismissHelp = React.useCallback((id: string) => {
    writeDismissed(id);
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    setLiveSignal((current) => (current?.id === id ? null : current));
  }, []);

  const sendPrompt = React.useCallback(async (raw: string) => {
    const text = raw.replace(/\s+/g, ' ').trim().slice(0, 2000);
    if (!text || loading || !accessToken) {
      return;
    }
    setOpen(true);
    if (activeSignal) {
      dismissHelp(activeSignal.id);
    }
    setMessages((prev) => [...prev, { role: 'user', text }]);
    setLoading(true);
    try {
      const response = await fetch(`${apiBase.replace(/\/$/, '')}/api/v1/console/assistant`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt: text,
          session_id: sessionId.current,
          language,
          active_page: activePage,
        }),
      });
      const body = response.ok ? await response.json() : null;
      const reply = typeof body?.reply === 'string' && body.reply.trim()
        ? body.reply.trim()
        : (es
          ? 'No pude responder ahora. Intente de nuevo o abra Ayuda en el paso de la lista.'
          : 'I could not answer just now. Try again, or open Help on the checklist step.');
      setMessages((prev) => [...prev, { role: 'assistant', text: reply, refused: Boolean(body?.refused) }]);
    } catch {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        text: es
          ? 'Algo falló al hablar con el asistente. Intente de nuevo.'
          : 'Something went wrong talking to the assistant. Try again.',
      }]);
    } finally {
      setLoading(false);
    }
  }, [accessToken, activePage, activeSignal, apiBase, dismissHelp, es, language, loading]);

  React.useEffect(() => {
    const onAsk = (event: Event) => {
      const detail = (event as CustomEvent<{ prompt?: string }>).detail;
      const prompt = String(detail?.prompt || '').trim();
      if (prompt) {
        void sendPrompt(prompt);
      } else {
        setOpen(true);
      }
    };
    const onHelp = (event: Event) => {
      const detail = (event as CustomEvent<ConsoleHelpSignal>).detail;
      if (!detail?.id) {
        return;
      }
      if (readDismissed(detail.id)) {
        return;
      }
      // Defer so callers can fire during the same tick as a React update.
      window.queueMicrotask(() => setLiveSignal(detail));
    };
    window.addEventListener(ASK_EVENT, onAsk as EventListener);
    window.addEventListener(HELP_SIGNAL_EVENT, onHelp as EventListener);
    return () => {
      window.removeEventListener(ASK_EVENT, onAsk as EventListener);
      window.removeEventListener(HELP_SIGNAL_EVENT, onHelp as EventListener);
    };
  }, [sendPrompt]);

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const text = input;
    setInput('');
    void sendPrompt(text);
  };

  const tipText = activeSignal
    ? (es ? activeSignal.tipEs : activeSignal.tipEn)
    : '';
  const tipPrompt = activeSignal
    ? (es ? (activeSignal.promptEs || activeSignal.promptEn) : (activeSignal.promptEn || activeSignal.promptEs))
    : '';

  return (
    <div className={`console-assistant${open ? ' is-open' : ''}${needsHelp ? ' is-needs-help' : ''}`}>
      {needsHelp && tipText ? (
        <div className="console-assistant-nudge" role="status">
          <p id="console-assistant-nudge-text">{tipText}</p>
          <div className="console-assistant-nudge-actions">
            <button
              type="button"
              className="console-assistant-nudge-ask"
              onClick={() => {
                if (tipPrompt) {
                  void sendPrompt(tipPrompt);
                } else {
                  setOpen(true);
                }
              }}
            >
              {es ? 'Preguntar al asistente' : 'Ask the assistant'}
            </button>
            <button
              type="button"
              className="console-assistant-nudge-dismiss"
              aria-label={es ? 'Descartar sugerencia' : 'Dismiss suggestion'}
              onClick={() => {
                if (activeSignal) {
                  dismissHelp(activeSignal.id);
                }
              }}
            >
              {es ? 'Ahora no' : 'Not now'}
            </button>
          </div>
        </div>
      ) : null}
      {open ? (
        <section className="console-assistant-panel" aria-label={es ? 'Asistente de consola' : 'Console assistant'}>
          <header className="console-assistant-head">
            <div>
              <h2>{es ? 'Asistente de NextCompli' : 'NextCompli assistant'}</h2>
              <p>{es ? 'Respuestas según lo que esta cuenta puede ver.' : 'Answers match what this login can see.'}</p>
            </div>
            <button
              type="button"
              className="console-assistant-close"
              aria-label={es ? 'Cerrar asistente' : 'Close assistant'}
              onClick={() => setOpen(false)}
            >
              ×
            </button>
          </header>
          <div className="console-assistant-body" ref={bodyRef}>
            {messages.map((message, index) => (
              <article
                key={`${message.role}-${index}`}
                className={`console-assistant-msg is-${message.role}${message.refused ? ' is-refused' : ''}`}
              >
                {message.text}
              </article>
            ))}
            {loading ? (
              <article className="console-assistant-msg is-assistant is-loading">
                {es ? 'Pensando…' : 'Thinking…'}
              </article>
            ) : null}
          </div>
          <div className="console-assistant-suggestions">
            {defaults.map((prompt) => (
              <button
                key={prompt}
                type="button"
                disabled={loading}
                onClick={() => void sendPrompt(prompt)}
              >
                {prompt}
              </button>
            ))}
          </div>
          <form className="console-assistant-form" onSubmit={onSubmit}>
            <label className="visually-hidden" htmlFor="console-assistant-input">
              {es ? 'Su pregunta' : 'Your question'}
            </label>
            <textarea
              id="console-assistant-input"
              value={input}
              rows={2}
              placeholder={es ? 'Pregunte al asistente…' : 'Ask the assistant…'}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  onSubmit(event);
                }
              }}
            />
            <button type="submit" disabled={loading || !input.trim()}>
              {es ? 'Enviar' : 'Send'}
            </button>
          </form>
        </section>
      ) : null}
      <button
        type="button"
        className={`console-assistant-launch${needsHelp ? ' is-attention' : ''}`}
        aria-expanded={open}
        aria-describedby={needsHelp && tipText ? 'console-assistant-nudge-text' : undefined}
        onClick={() => setOpen((value) => !value)}
      >
        {open
          ? (es ? 'Cerrar ayuda' : 'Close help')
          : (es ? '¿Necesita ayuda?' : 'Need help?')}
      </button>
    </div>
  );
};

export default ConsoleAssistant;
